<template>
  <div class="kyc-page">
    <v-container class="kyc-container pa-0" fluid>
      <v-card class="kyc-shell" elevation="0">

        <!-- HEADER -->
        <div class="kyc-header">
          <div>
            <div class="header-overline">Account Verification</div>
            <h1 class="header-title">Identity Verification</h1>
            <p class="header-subtitle">
              Complete all 3 steps. Face comparison runs directly on your device — your video never leaves your phone until faces match.
            </p>
          </div>
          <v-chip :color="statusChipColor" small outlined class="header-chip">
            <v-icon left x-small>{{ statusChipIcon }}</v-icon>
            {{ statusChipLabel }}
          </v-chip>
        </div>

        <!-- STATUS BANNERS -->
        <div v-if="kycStatus === 'approved'" class="status-banner status-banner--approved">
          <v-icon color="green darken-2" size="28" class="mr-3">mdi-shield-check</v-icon>
          <div>
            <div class="banner-title">Identity Verified</div>
            <div class="banner-sub">Your account is fully active.</div>
          </div>
        </div>

        <div v-else-if="kycStatus === 'pending'" class="status-banner status-banner--pending">
          <v-icon color="orange darken-2" size="28" class="mr-3">mdi-clock-outline</v-icon>
          <div>
            <div class="banner-title">Application Under Review</div>
            <div class="banner-sub">Our team is processing your file. Response within 24 hours.</div>
          </div>
          <v-btn text small color="orange darken-2" class="ml-auto" :loading="checkingStatus" @click="fetchKYCStatus">
            <v-icon left small>mdi-refresh</v-icon>Check Status
          </v-btn>
        </div>

        <div v-else-if="kycStatus === 'rejected'" class="status-banner status-banner--rejected">
          <v-icon color="red darken-2" size="28" class="mr-3">mdi-alert-circle-outline</v-icon>
          <div>
            <div class="banner-title">Application Rejected</div>
            <div v-if="reviewNote" class="banner-sub">Reason: {{ reviewNote }}</div>
          </div>
        </div>

        <!-- ──────────────────── WIZARD ──────────────────── -->
        <template v-if="kycStatus === 'none' || kycStatus === 'rejected'">

          <!-- Stepper Nav -->
          <div class="stepper-nav">
            <div
              v-for="(step, i) in steps"
              :key="i"
              class="stepper-item"
              :class="{
                'stepper-item--completed': stepStates[i] === 'completed',
                'stepper-item--active':    stepStates[i] === 'active',
                'stepper-item--denied':    stepStates[i] === 'denied',
                'stepper-item--locked':    stepStates[i] === 'locked'
              }"
            >
              <div class="stepper-node">
                <v-icon v-if="stepStates[i] === 'completed'" x-small color="white">mdi-check</v-icon>
                <v-icon v-else-if="stepStates[i] === 'denied'" x-small color="white">mdi-close</v-icon>
                <span v-else>{{ i + 1 }}</span>
              </div>
              <span class="stepper-label">{{ step.label }}</span>
            </div>
          </div>

          <!-- ══ STEP 1 : ID PHOTO ══ -->
          <div v-if="currentStep === 0" class="step-panel">
            <div class="step-header">
              <div class="step-number">1</div>
              <div>
                <h2 class="step-title">National ID Photo</h2>
                <p class="step-desc">Take a live photo of your ID card. Hold it flat and steady in good light.</p>
              </div>
            </div>

            <div class="capture-stage">
              <div v-if="idPhotoDenied" class="denied-state">
                <v-icon color="red darken-2" size="40" class="mb-3">mdi-camera-off-outline</v-icon>
                <h3 class="denied-title">Camera Access Denied</h3>
                <p class="denied-text">Camera access is required to capture your ID. Enable it in your browser settings.</p>
                <v-btn color="primary" outlined rounded @click="retryIdPhoto">
                  <v-icon left small>mdi-refresh</v-icon>Try Again
                </v-btn>
              </div>

              <div v-else-if="idCardPreview" class="preview-stage">
                <div class="preview-frame">
                  <img :src="idCardPreview" class="captured-image" alt="ID Capture" />
                  <div class="preview-badge"><v-icon x-small color="white">mdi-check</v-icon> Captured</div>
                </div>
                <div class="preview-actions">
                  <v-btn text color="grey darken-1" @click="resetIdPhoto">
                    <v-icon left small>mdi-refresh</v-icon>Retake
                  </v-btn>
                  <v-btn color="primary" depressed @click="confirmIdPhoto">
                    <v-icon left small>mdi-check</v-icon>Confirm & Continue
                  </v-btn>
                </div>
              </div>

              <div v-else-if="idCameraActive" class="camera-stage">
                <div class="camera-frame">
                  <video ref="idCameraStream" autoplay muted playsinline class="camera-feed" />
                  <div class="camera-guides">
                    <div class="id-guide-rect"><span class="guide-label">Align ID card here</span></div>
                  </div>
                </div>
                <div class="camera-actions">
                  <v-btn text small color="grey darken-1" @click="stopIdCamera">Cancel</v-btn>
                  <v-btn color="primary" fab dark @click="captureIdPhoto">
                    <v-icon>mdi-camera</v-icon>
                  </v-btn>
                  <div style="width:64px" />
                </div>
              </div>

              <div v-else class="start-stage">
                <div class="start-icon-wrap">
                  <v-icon color="primary" size="48">mdi-card-account-details-outline</v-icon>
                </div>
                <h3 class="start-title">Live ID Capture</h3>
                <p class="start-hint">Make sure you're in a well-lit area and your ID is clean and readable.</p>
                <v-btn color="primary" large rounded depressed min-width="220" @click="startIdCamera">
                  <v-icon left>mdi-camera</v-icon>Open Camera
                </v-btn>
                <p class="start-microhint">Camera permission will be requested</p>
              </div>
            </div>
          </div>

          <!-- ══ STEP 2 : SELFIE VIDEO + FACE COMPARISON (in-browser) ══ -->
          <div v-else-if="currentStep === 1" class="step-panel">
            <div class="step-header">
              <div class="step-number step-number--2">2</div>
              <div>
                <h2 class="step-title">Selfie Video</h2>
                <p class="step-desc">Record 3–10 seconds facing the camera. Face matching runs locally on your device.</p>
              </div>
            </div>

            <!-- Models loading bar -->
            <div v-if="modelsLoading" class="models-loader">
              <div class="models-loader-inner">
                <v-progress-linear indeterminate color="primary" rounded height="4" />
                <div class="models-loader-text">
                  <v-icon small color="primary" class="mr-1">mdi-cpu-64-bit</v-icon>
                  Loading face recognition models on your device…
                </div>
              </div>
            </div>

            <div class="capture-stage" :class="{ 'capture-stage--analyzing': faceAnalyzing }">

              <!-- Denied -->
              <div v-if="videoDenied" class="denied-state">
                <v-icon color="red darken-2" size="40" class="mb-3">mdi-video-off-outline</v-icon>
                <h3 class="denied-title">Camera Access Denied</h3>
                <p class="denied-text">Video recording is mandatory for identity verification.</p>
                <v-btn color="primary" outlined rounded @click="retryVideo">
                  <v-icon left small>mdi-refresh</v-icon>Try Again
                </v-btn>
              </div>

              <!-- Analyzing (face-api.js running) -->
              <div v-else-if="faceAnalyzing" class="analyzing-state">
                <div class="analyzing-ring">
                  <v-progress-circular indeterminate color="primary" size="72" width="4" />
                  <v-icon color="primary" size="28" class="analyzing-icon">mdi-face-recognition</v-icon>
                </div>
                <h3 class="analyzing-title">Comparing faces on your device…</h3>
                <p class="analyzing-sub">Using your device's processor — no data sent yet.</p>
                <div class="analyzing-steps">
                  <div class="analyzing-step" :class="{ done: analysisStepDone[0] }">
                    <v-icon x-small>{{ analysisStepDone[0] ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                    Detecting face on ID card
                  </div>
                  <div class="analyzing-step" :class="{ done: analysisStepDone[1] }">
                    <v-icon x-small>{{ analysisStepDone[1] ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                    Detecting face in selfie
                  </div>
                  <div class="analyzing-step" :class="{ done: analysisStepDone[2] }">
                    <v-icon x-small>{{ analysisStepDone[2] ? 'mdi-check-circle' : 'mdi-circle-outline' }}</v-icon>
                    Computing similarity score
                  </div>
                </div>
              </div>

              <!-- Face mismatch (local rejection) -->
              <div v-else-if="faceLocalResult === 'mismatch'" class="denied-state">
                <v-icon color="red darken-2" size="40" class="mb-3">mdi-face-off-outline</v-icon>
                <h3 class="denied-title">Faces Don't Match</h3>
                <p class="denied-text">
                  The face in the selfie does not match the ID card photo
                  (similarity: <strong>{{ faceLocalScore }}%</strong>).<br/>
                  Make sure you're using YOUR own ID, face the camera directly, and ensure good lighting.
                </p>
                <v-btn color="primary" outlined rounded @click="resetVideoForRetry">
                  <v-icon left small>mdi-refresh</v-icon>Try Again
                </v-btn>
              </div>

              <!-- Face match confirmed -->
              <div v-else-if="faceLocalResult === 'match'" class="preview-stage">
                <div class="face-match-badge">
                  <v-icon color="green darken-1" size="32" class="mr-2">mdi-face-recognition</v-icon>
                  <div>
                    <div class="face-match-title">Face Match Confirmed</div>
                    <div class="face-match-sub">Similarity: {{ faceLocalScore }}% — verified on your device</div>
                  </div>
                </div>
                <div class="preview-frame preview-frame--video mt-3">
                  <video :src="videoPreviewUrl" controls class="captured-video" />
                  <div class="preview-badge"><v-icon x-small color="white">mdi-check</v-icon> Recorded</div>
                </div>
                <div class="preview-actions">
                  <v-btn text color="grey darken-1" @click="resetVideo">
                    <v-icon left small>mdi-refresh</v-icon>Retake
                  </v-btn>
                  <v-btn color="primary" depressed @click="confirmVideo">
                    <v-icon left small>mdi-arrow-right</v-icon>Continue
                  </v-btn>
                </div>
              </div>

              <!-- Recording -->
              <div v-else-if="cameraActive" class="camera-stage">
                <div class="camera-frame">
                  <video ref="cameraStream" autoplay muted playsinline class="camera-feed" />
                  <div class="camera-guides">
                    <div class="face-guide-oval" />
                  </div>
                  <div v-if="recording" class="recording-pill">
                    <v-icon x-small color="red" class="blink mr-1">mdi-circle</v-icon>
                    {{ recordingTimer }}s / 10s max
                  </div>
                </div>
                <div class="camera-actions">
                  <v-btn text small color="grey darken-1" @click="stopCamera">Cancel</v-btn>
                  <v-btn v-if="!recording" color="red" fab dark @click="startRecording">
                    <v-icon>mdi-record</v-icon>
                  </v-btn>
                  <v-btn v-else color="white" fab @click="stopRecording">
                    <v-icon color="red">mdi-stop</v-icon>
                  </v-btn>
                  <div style="width:64px" />
                </div>
              </div>

              <!-- Start -->
              <div v-else class="start-stage">
                <div class="start-icon-wrap start-icon-wrap--2">
                  <v-icon color="#5c35c9" size="48">mdi-account-box-outline</v-icon>
                </div>
                <h3 class="start-title">Live Selfie Recording</h3>
                <p class="start-hint">
                  Your face will be compared to the ID card <strong>directly on this device</strong>.<br/>
                  No video is sent to the server until faces match.
                </p>
                <v-btn color="primary" large rounded depressed min-width="220" :disabled="modelsLoading" @click="openCamera">
                  <v-icon left>mdi-video</v-icon>Start Recording
                </v-btn>
                <p class="start-microhint">{{ modelsLoading ? 'Loading AI models…' : 'Camera permission will be requested' }}</p>
              </div>
            </div>
          </div>

          <!-- ══ STEP 3 : GEOLOCATION ══ -->
          <div v-else-if="currentStep === 2" class="step-panel">
            <div class="step-header">
              <div class="step-number step-number--3">3</div>
              <div>
                <h2 class="step-title">Location Verification</h2>
                <p class="step-desc">Confirm your physical presence. Location is recorded once for legal purposes.</p>
              </div>
            </div>

            <div class="capture-stage">
              <div v-if="geoStatus === 'denied'" class="denied-state">
                <v-icon color="red darken-2" size="40" class="mb-3">mdi-map-marker-off-outline</v-icon>
                <h3 class="denied-title">Location Access Denied</h3>
                <p class="denied-text">Location is required to complete KYC. Enable it in your browser settings.</p>
                <v-btn color="primary" outlined rounded @click="resetGeo">
                  <v-icon left small>mdi-refresh</v-icon>Try Again
                </v-btn>
              </div>

              <div v-else-if="geoStatus === 'ok'" class="geo-success-panel">
                <div class="geo-card">
                  <div class="geo-card-icon">
                    <v-icon color="green darken-1" size="32">mdi-map-marker-check</v-icon>
                  </div>
                  <div class="geo-card-body">
                    <div class="geo-card-title">Location Confirmed</div>
                    <div v-if="geoAddress.city || geoAddress.country" class="geo-card-address">
                      <span v-if="geoAddress.city">{{ geoAddress.city }}</span>
                      <span v-if="geoAddress.country">, {{ geoAddress.country }}</span>
                      <img
                        v-if="geoAddress.countryCode"
                        :src="`https://flagcdn.com/16x12/${geoAddress.countryCode.toLowerCase()}.png`"
                        class="flag-img"
                      />
                    </div>
                    <div class="geo-card-coords">{{ geoLatitude.toFixed(5) }}, {{ geoLongitude.toFixed(5) }}</div>
                  </div>
                </div>
                <div class="preview-actions" style="justify-content:center">
                  <v-btn text color="grey darken-1" @click="resetGeo">
                    <v-icon left small>mdi-refresh</v-icon>Change
                  </v-btn>
                  <v-btn color="primary" depressed @click="confirmGeo">
                    <v-icon left small>mdi-check</v-icon>Confirm & Finish
                  </v-btn>
                </div>
              </div>

              <div v-else-if="geoStatus === 'loading' || geoStatus === 'geocoding'" class="start-stage">
                <v-progress-circular indeterminate color="primary" size="48" width="3" class="mb-4" />
                <h3 class="start-title">{{ geoStatus === 'geocoding' ? 'Identifying location…' : 'Getting position…' }}</h3>
              </div>

              <div v-else class="start-stage">
                <div class="start-icon-wrap start-icon-wrap--3">
                  <v-icon color="#00897b" size="48">mdi-map-marker-radius-outline</v-icon>
                </div>
                <h3 class="start-title">Share Your Location</h3>
                <p class="start-hint">Required to verify your physical presence at the time of submission.</p>
                <v-btn color="primary" large rounded depressed min-width="220" @click="requestGeo">
                  <v-icon left>mdi-crosshairs-gps</v-icon>Share Location
                </v-btn>
                <p class="start-microhint">Location permission will be requested</p>
              </div>
            </div>
          </div>

          <!-- ══ STEP 4 : REVIEW & SUBMIT ══ -->
          <div v-else-if="currentStep === 3" class="step-panel">
            <div class="step-header">
              <div class="step-number step-number--done">✓</div>
              <div>
                <h2 class="step-title">Review & Submit</h2>
                <p class="step-desc">All steps completed. Review and submit for final server-side validation.</p>
              </div>
            </div>

            <div class="review-stage">
              <!-- Summary cards -->
              <div class="review-cards">
                <div class="review-item review-item--ok">
                  <div class="review-thumb">
                    <img :src="idCardPreview" alt="ID" />
                  </div>
                  <div class="review-meta">
                    <div class="review-label">National ID</div>
                    <div class="review-status"><v-icon x-small color="green">mdi-check</v-icon> Captured live</div>
                  </div>
                </div>

                <div class="review-item review-item--ok">
                  <div class="review-thumb review-thumb--video">
                    <video :src="videoPreviewUrl" />
                    <div class="review-play"><v-icon small color="white">mdi-play</v-icon></div>
                  </div>
                  <div class="review-meta">
                    <div class="review-label">Selfie Video</div>
                    <div class="review-status">
                      <v-icon x-small color="green">mdi-check</v-icon>
                      Face match confirmed on device ({{ faceLocalScore }}%)
                    </div>
                  </div>
                </div>

                <div class="review-item review-item--ok">
                  <div class="review-thumb review-thumb--geo">
                    <v-icon color="primary" size="24">mdi-map-marker</v-icon>
                  </div>
                  <div class="review-meta">
                    <div class="review-label">Location</div>
                    <div class="review-status">
                      <v-icon x-small color="green">mdi-check</v-icon>
                      {{ geoAddress.city || 'Recorded' }}<span v-if="geoAddress.country">, {{ geoAddress.country }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <v-alert dense text type="info" class="review-disclaimer mt-4">
                By submitting, you confirm all data was captured live and you are the legitimate owner of this document.
                This submission is stored for legal compliance purposes.
              </v-alert>

              <div class="submit-wrap">
                <v-btn
                  color="primary"
                  x-large
                  rounded
                  depressed
                  min-width="280"
                  :loading="loading"
                  @click="submitKYC"
                >
                  <v-icon left>mdi-send-outline</v-icon>
                  Submit Application
                </v-btn>
              </div>
            </div>
          </div>

          <!-- Result banner -->
          <v-row v-if="result" class="ma-0 mb-4">
            <v-col cols="12" class="pa-3 pa-md-4 pt-0">
              <v-alert
                :type="result.category === 'pending' ? 'info' : result.category === 'success' ? 'success' : 'error'"
                border="left" colored-border elevation="1" class="result-alert"
              >
                {{ result.text }}
              </v-alert>
            </v-col>
          </v-row>

        </template>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios'

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/reverse'

// face-api.js CDN — chargé une seule fois dans loadFaceModels()
const FACE_API_CDN  = 'https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js'
const MODELS_URL    = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model/'

// Seuil : distance Euclidienne entre descripteurs (< 0.50 = même personne)
// Plus strict = moins de faux positifs. Ajustez si besoin.
const FACE_DISTANCE_THRESHOLD = 0.50

export default {
  name: 'IdentityVerification',

  data() {
    return {
      loading:        false,
      checkingStatus: false,

      // Wizard
      currentStep: 0,
      stepStates: ['active', 'locked', 'locked'],
      steps: [{ label: 'ID Photo' }, { label: 'Selfie' }, { label: 'Location' }],

      // Step 1 — ID Photo
      idCameraActive:  false,
      idPhotoDenied:   false,
      idCardFile:      null,
      idCardPreview:   null,
      idMediaStream:   null,

      // Step 2 — Video + face-api.js
      cameraActive:       false,
      videoDenied:        false,
      videoFile:          null,
      videoPreviewUrl:    null,
      mediaStream:        null,
      mediaRecorder:      null,
      recordedChunks:     [],
      recording:          false,
      recordingTimer:     0,
      recordingInterval:  null,

      // face-api.js state
      modelsLoading:      false,
      modelsReady:        false,
      faceAnalyzing:      false,
      faceLocalResult:    null,   // null | 'match' | 'mismatch'
      faceLocalScore:     0,      // 0–100
      faceLocalDistance:  null,
      analysisStepDone:   [false, false, false],

      // Step 3 — Géolocation
      geoStatus:    'idle',
      geoLatitude:  null,
      geoLongitude: null,
      geoAddress:   { neighbourhood: null, suburb: null, city: null, state: null, country: null, countryCode: null, displayName: null },

      // KYC
      kycStatus:  'none',
      reviewNote: '',
      result:     null,
    }
  },

  computed: {
    canSubmit() {
      return !!this.idCardFile && !!this.videoFile &&
             this.faceLocalResult === 'match' &&
             this.geoStatus === 'ok' && !this.loading
    },
    statusChipColor() {
      return { approved: 'green', pending: 'orange', rejected: 'red', none: 'primary' }[this.kycStatus] || 'primary'
    },
    statusChipIcon() {
      return { approved: 'mdi-shield-check', pending: 'mdi-clock-outline', rejected: 'mdi-alert-circle-outline', none: 'mdi-shield-outline' }[this.kycStatus] || 'mdi-shield-outline'
    },
    statusChipLabel() {
      return { approved: 'Verified', pending: 'Under Review', rejected: 'Rejected', none: 'Verification Required' }[this.kycStatus] || 'Verification Required'
    },
  },

  mounted() {
    this.$store.commit('setCloseURL', '/user_profile/account')
    this.fetchKYCStatus()
    this.loadFaceModels()   // préchargement silencieux dès le montage
  },

  beforeDestroy() {
    this.stopIdCamera()
    this.stopCamera()
    if (this.videoPreviewUrl) URL.revokeObjectURL(this.videoPreviewUrl)
  },

  methods: {

    // ════════════════════════════════════════════════════════
    // FACE-API.JS — Chargement des modèles
    // ════════════════════════════════════════════════════════

    async loadFaceModels() {
      if (this.modelsReady || this.modelsLoading) return
      this.modelsLoading = true

      try {
        // 1. Charger le script face-api.js si pas encore présent
        if (!window.faceapi) {
          await new Promise((resolve, reject) => {
            const s = document.createElement('script')
            s.src = FACE_API_CDN
            s.onload  = resolve
            s.onerror = reject
            document.head.appendChild(s)
          })
        }

        const faceapi = window.faceapi

        // 2. Charger les 3 modèles nécessaires (~12 Mo total, mis en cache par le navigateur)
        await Promise.all([
          faceapi.nets.ssdMobilenetv1.loadFromUri(MODELS_URL),        // détection visage
          faceapi.nets.faceLandmark68Net.loadFromUri(MODELS_URL),     // points du visage
          faceapi.nets.faceRecognitionNet.loadFromUri(MODELS_URL),    // embeddings 128D
        ])

        this.modelsReady = true
        console.log('[KYC] face-api.js models loaded ✓')
      } catch (err) {
        console.error('[KYC] Failed to load face-api models:', err)
        // Pas bloquant : la vérification se fera côté serveur uniquement
      } finally {
        this.modelsLoading = false
      }
    },

    // ════════════════════════════════════════════════════════
    // FACE-API.JS — Comparaison locale (téléphone / PC)
    // ════════════════════════════════════════════════════════

    async compareFacesLocally(idImageSrc, videoFile) {
      if (!this.modelsReady) {
        // Modèles non chargés → on laisse passer et le serveur vérifie
        console.warn('[KYC] Models not ready, skipping local check')
        this.faceLocalResult   = 'match'
        this.faceLocalScore    = 0
        this.faceLocalDistance = null
        return
      }

      this.faceAnalyzing      = true
      this.analysisStepDone   = [false, false, false]
      const faceapi           = window.faceapi

      try {
        // ── A. Détecter le visage sur la CNI ──
        const idImg = await this._imageFromSrc(idImageSrc)
        const idDetection = await faceapi
          .detectSingleFace(idImg, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.5 }))
          .withFaceLandmarks()
          .withFaceDescriptor()

        this.analysisStepDone = [true, false, false]

        if (!idDetection) {
          this.faceAnalyzing   = false
          this.faceLocalResult = 'mismatch'
          this.faceLocalScore  = 0
          this._snack('No face detected on the ID card. Please retake it in better lighting.', 'orange darken-3')
          return
        }

        // ── B. Extraire frames de la vidéo et détecter un visage ──
        const frames = await this._extractVideoFrames(videoFile, 6)
        this.analysisStepDone = [true, true, false]

        let bestDescriptor = null
        let bestScore      = 0

        for (const canvas of frames) {
          try {
            const det = await faceapi
              .detectSingleFace(canvas, new faceapi.SsdMobilenetv1Options({ minConfidence: 0.5 }))
              .withFaceLandmarks()
              .withFaceDescriptor()

            if (!det) continue

            const dist  = faceapi.euclideanDistance(idDetection.descriptor, det.descriptor)
            const score = Math.max(0, Math.round((1 - dist) * 100))
            if (score > bestScore) {
              bestScore      = score
              bestDescriptor = det.descriptor
              this.faceLocalDistance = dist
            }
          } catch { /* frame inutilisable */ }
        }

        this.analysisStepDone = [true, true, true]
        await this._sleep(400)   // légère pause pour que l'utilisateur voit l'étape 3

        if (!bestDescriptor) {
          this.faceAnalyzing   = false
          this.faceLocalResult = 'mismatch'
          this.faceLocalScore  = 0
          this._snack('No clear face detected in the video. Face the camera directly.', 'orange darken-3')
          return
        }

        // ── C. Décision ──
        const distance      = this.faceLocalDistance
        this.faceLocalScore = bestScore
        this.faceLocalResult = distance < FACE_DISTANCE_THRESHOLD ? 'match' : 'mismatch'

        console.log(`[KYC] Local face distance: ${distance.toFixed(4)} | score: ${bestScore}% | result: ${this.faceLocalResult}`)

      } catch (err) {
        console.error('[KYC] Face comparison error:', err)
        // Erreur inattendue → on laisse passer vers le serveur
        this.faceLocalResult   = 'match'
        this.faceLocalScore    = 0
        this.faceLocalDistance = null
      } finally {
        this.faceAnalyzing = false
      }
    },

    // Crée un HTMLImageElement depuis une data URL
    _imageFromSrc(src) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload  = () => resolve(img)
        img.onerror = reject
        img.src     = src
      })
    },

    // Extrait N frames équidistantes d'un fichier vidéo → tableau de Canvas
    _extractVideoFrames(videoFile, n = 6) {
      return new Promise((resolve) => {
        const url     = URL.createObjectURL(videoFile)
        const video   = document.createElement('video')
        const frames  = []
        let captured  = 0

        video.preload  = 'metadata'
        video.muted    = true
        video.src      = url

        video.onloadedmetadata = () => {
          const duration = video.duration || 3
          const times    = Array.from({ length: n }, (_, i) =>
            0.1 + (i / (n - 1)) * duration * 0.85
          )

          const capture = (index) => {
            if (index >= times.length) {
              URL.revokeObjectURL(url)
              resolve(frames)
              return
            }
            video.currentTime = times[index]
          }

          video.onseeked = () => {
            const canvas = document.createElement('canvas')
            canvas.width  = video.videoWidth  || 640
            canvas.height = video.videoHeight || 480
            canvas.getContext('2d').drawImage(video, 0, 0)
            frames.push(canvas)
            captured++
            capture(captured)
          }

          capture(0)
        }

        video.onerror = () => {
          URL.revokeObjectURL(url)
          resolve(frames)
        }
      })
    },

    _sleep(ms) { return new Promise(r => setTimeout(r, ms)) },

    // ════════════════════════════════════════════════════════
    // Navigation
    // ════════════════════════════════════════════════════════

    completeStep(index) {
      this.$set(this.stepStates, index, 'completed')
      if (index + 1 < this.stepStates.length) {
        this.$set(this.stepStates, index + 1, 'active')
        this.currentStep = index + 1
      } else {
        this.currentStep = index + 1
      }
    },

    // ════════════════════════════════════════════════════════
    // KYC Status
    // ════════════════════════════════════════════════════════

    async fetchKYCStatus() {
      this.checkingStatus = true
      try {
        const res = await axios.get('/api/v1/home/kyc/status/')
        const data = res.data
        this.kycStatus = data.kyc_status === 'not_submitted' ? 'none' : data.kyc_status
        this.reviewNote = data.rejection_reason || ''
        if (this.$store.state.user_info?.user_profile) {
          this.$store.state.user_info.user_profile.kyc_status = this.kycStatus
        }
      } catch {
        this._snack('Unable to check status. Please refresh.', 'orange darken-3')
      } finally {
        this.checkingStatus = false
      }
    },

    // ════════════════════════════════════════════════════════
    // STEP 1 — ID Photo
    // ════════════════════════════════════════════════════════

    async startIdCamera() {
      this.idPhotoDenied = false
      try {
        this.idMediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        })
        this.idCameraActive = true
        this.$nextTick(() => {
          if (this.$refs.idCameraStream) this.$refs.idCameraStream.srcObject = this.idMediaStream
        })
      } catch {
        this.idPhotoDenied = true
        this._snack('Camera access denied. Enable it in browser settings.', 'red darken-3')
      }
    },

    captureIdPhoto() {
      const video = this.$refs.idCameraStream
      if (!video) return
      const canvas  = document.createElement('canvas')
      canvas.width  = video.videoWidth  || 1280
      canvas.height = video.videoHeight || 720
      canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)
      canvas.toBlob((blob) => {
        if (!blob) return
        this.idCardFile    = new File([blob], 'id_card_live.jpg', { type: 'image/jpeg' })
        this.idCardPreview = URL.createObjectURL(blob)
        this.stopIdCamera()
      }, 'image/jpeg', 0.92)
    },

    stopIdCamera() {
      if (this.idMediaStream) { this.idMediaStream.getTracks().forEach(t => t.stop()); this.idMediaStream = null }
      this.idCameraActive = false
    },

    resetIdPhoto() {
      this.idCardFile = null
      if (this.idCardPreview) URL.revokeObjectURL(this.idCardPreview)
      this.idCardPreview = null
      this.idPhotoDenied = false
      this.$set(this.stepStates, 0, 'active')
    },

    retryIdPhoto() { this.idPhotoDenied = false; this.$set(this.stepStates, 0, 'active'); this.startIdCamera() },

    confirmIdPhoto() {
      if (!this.idCardFile) return
      this.completeStep(0)
    },

    // ════════════════════════════════════════════════════════
    // STEP 2 — Selfie Video
    // ════════════════════════════════════════════════════════

    async openCamera() {
      this.videoDenied = false
      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        })
        this.cameraActive = true
        this.$nextTick(() => {
          if (this.$refs.cameraStream) this.$refs.cameraStream.srcObject = this.mediaStream
        })
      } catch {
        this.videoDenied = true
        this._snack('Camera access denied.', 'red darken-3')
      }
    },

    startRecording() {
      if (!this.mediaStream) return
      this.recordedChunks = []
      this.recordingTimer = 0
      const mime  = MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm'
      this.mediaRecorder = new MediaRecorder(this.mediaStream, { mimeType: mime })
      this.mediaRecorder.ondataavailable = e => { if (e.data.size > 0) this.recordedChunks.push(e.data) }
      this.mediaRecorder.onstop = this._processRecordedVideo
      this.mediaRecorder.start(100)
      this.recording = true
      this.recordingInterval = setInterval(() => { this.recordingTimer++; if (this.recordingTimer >= 10) this.stopRecording() }, 1000)
    },

    stopRecording() {
      if (this.mediaRecorder && this.recording) {
        this.mediaRecorder.stop()
        this.recording = false
        clearInterval(this.recordingInterval)
      }
    },

    async _processRecordedVideo() {
      const blob = new Blob(this.recordedChunks, { type: 'video/webm' })
      this.videoFile       = new File([blob], 'selfie_kyc.webm', { type: 'video/webm' })
      if (this.videoPreviewUrl) URL.revokeObjectURL(this.videoPreviewUrl)
      this.videoPreviewUrl = URL.createObjectURL(blob)
      this.stopCamera()

      // ── Lancer la comparaison locale immédiatement ──
      await this.compareFacesLocally(this.idCardPreview, this.videoFile)
    },

    stopCamera() {
      if (this.mediaStream) { this.mediaStream.getTracks().forEach(t => t.stop()); this.mediaStream = null }
      this.cameraActive = false
      this.recording    = false
      clearInterval(this.recordingInterval)
    },

    resetVideo() {
      this.videoFile      = null
      this.faceLocalResult = null
      this.faceLocalScore  = 0
      if (this.videoPreviewUrl) URL.revokeObjectURL(this.videoPreviewUrl)
      this.videoPreviewUrl = null
      this.videoDenied     = false
    },

    resetVideoForRetry() {
      this.resetVideo()
    },

    confirmVideo() {
      if (!this.videoFile || this.faceLocalResult !== 'match') return
      this.completeStep(1)
    },

    // ════════════════════════════════════════════════════════
    // STEP 3 — Géolocalisation
    // ════════════════════════════════════════════════════════

    requestGeo() {
      if (!navigator.geolocation) { this.geoStatus = 'denied'; return }
      this.geoStatus = 'loading'
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          this.geoLatitude  = pos.coords.latitude
          this.geoLongitude = pos.coords.longitude
          this.geoStatus    = 'geocoding'
          await this._reverseGeocode(this.geoLatitude, this.geoLongitude)
          this.geoStatus = 'ok'
        },
        () => { this.geoStatus = 'denied'; this._snack('Location denied.', 'red darken-3') },
        { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 }
      )
    },

    async _reverseGeocode(lat, lng) {
      try {
        const res  = await fetch(`${NOMINATIM_URL}?lat=${lat}&lon=${lng}&format=json&addressdetails=1`, {
          headers: { 'Accept-Language': 'en', 'User-Agent': 'KYC-App/1.0' },
        })
        if (!res.ok) return
        const data = await res.json()
        const addr = data.address || {}
        this.geoAddress = {
          neighbourhood: addr.neighbourhood || addr.quarter || null,
          suburb:        addr.suburb || null,
          city:          addr.city || addr.town || addr.village || addr.county || null,
          state:         addr.state || null,
          country:       addr.country || null,
          countryCode:   addr.country_code ? addr.country_code.toUpperCase() : null,
          displayName:   data.display_name || null,
        }
      } catch { /* silencieux */ }
    },

    resetGeo() {
      this.geoStatus   = 'idle'
      this.geoLatitude = this.geoLongitude = null
      this.geoAddress  = { neighbourhood: null, suburb: null, city: null, state: null, country: null, countryCode: null, displayName: null }
      this.$set(this.stepStates, 2, 'active')
    },

    confirmGeo() {
      if (this.geoStatus !== 'ok') return
      this.completeStep(2)
    },

    // ════════════════════════════════════════════════════════
    // SUBMIT (serveur : stockage juridique + validation doc)
    // ════════════════════════════════════════════════════════

    async submitKYC() {
      if (!this.canSubmit) return
      this.loading = true
      this.result  = null

      const formData = new FormData()
      formData.append('id_card',      this.idCardFile)
      formData.append('selfie_video', this.videoFile)
      formData.append('latitude',     this.geoLatitude)
      formData.append('longitude',    this.geoLongitude)

      // Score de comparaison locale (pour audit)
      formData.append('local_face_score',    this.faceLocalScore)
      formData.append('local_face_distance', this.faceLocalDistance ?? '')

      if (this.geoAddress.country)       formData.append('geo_country',       this.geoAddress.country)
      if (this.geoAddress.countryCode)   formData.append('geo_country_code',  this.geoAddress.countryCode)
      if (this.geoAddress.city)          formData.append('geo_city',          this.geoAddress.city)
      if (this.geoAddress.state)         formData.append('geo_state',         this.geoAddress.state)
      if (this.geoAddress.displayName)   formData.append('geo_display_name',  this.geoAddress.displayName)

      try {
        const res  = await axios.post('/api/v1/home/kyc/submit/', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
        const data = res.data[0]
        this.result    = data
        this.kycStatus = data.kyc_status || 'pending'
        this._snack(data.text, data.color)
        this.$store.commit('setSnackBarTimeOut', 8000)
        if (this.$store.state.user_info?.user_profile) {
          this.$store.state.user_info.user_profile.kyc_status = this.kycStatus
        }
      } catch (error) {
        const msg = error.response?.data?.[0]?.text || 'Network error. Please try again.'
        this._snack(msg, 'red darken-4')
        this.result = { text: msg, category: 'error' }
      } finally {
        this.loading = false
      }
    },

    _snack(message, color = 'primary') {
      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', color)
      this.$store.commit('activateSnackBar', true)
    },
  },
}
</script>

<style scoped>
.kyc-page { padding: 16px 8px 8px; background: #f0f4f8; min-height: 100vh; }
.kyc-container { max-width: 800px; margin: 0 auto; }

.kyc-shell {
  border-radius: 24px;
  background: #ffffff;
  border: 1px solid rgba(23,84,180,0.08);
  box-shadow: 0 20px 60px rgba(17,63,140,0.08);
  overflow: hidden;
  animation: fadeInUp 0.45s ease;
}

.kyc-header {
  display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
  padding: 32px 32px 24px;
  background: linear-gradient(135deg,#f7faff 0%,#ffffff 100%);
  border-bottom: 1px solid rgba(23,84,180,0.08);
}
.header-overline { font-size:.75rem; font-weight:700; text-transform:uppercase; letter-spacing:1.5px; color:#1976d2; margin-bottom:4px; }
.header-title { margin:0; font-size:1.6rem; font-weight:800; color:#0d2b5e; }
.header-subtitle { margin:8px 0 0; color:#6b7c93; font-size:.95rem; line-height:1.6; max-width:480px; }

.status-banner { display:flex; align-items:center; padding:20px 32px; font-weight:500; }
.status-banner--approved { background:rgba(56,142,60,.06); }
.status-banner--pending  { background:rgba(245,124,0,.06); }
.status-banner--rejected { background:rgba(211,47,47,.06); }
.banner-title { font-size:1rem; font-weight:700; color:#1a237e; }
.banner-sub   { font-size:.85rem; color:#546e7a; margin-top:2px; }

.stepper-nav { display:flex; align-items:center; justify-content:center; padding:24px 32px 0; }
.stepper-item { display:flex; flex-direction:column; align-items:center; gap:8px; flex:1; position:relative; max-width:160px; }
.stepper-item:not(:last-child)::after { content:''; position:absolute; top:16px; left:calc(50% + 20px); right:calc(-50% + 20px); height:2px; background:#e3eaf3; z-index:0; transition:background .3s; }
.stepper-item--completed:not(:last-child)::after, .stepper-item--active:not(:last-child)::after { background:linear-gradient(90deg,#1976d2,#42a5f5); }
.stepper-node { width:32px; height:32px; border-radius:50%; background:#e3eaf3; border:2px solid #e3eaf3; display:flex; align-items:center; justify-content:center; font-size:.8rem; font-weight:700; color:#90a4ae; z-index:1; transition:all .3s ease; }
.stepper-item--completed .stepper-node { background:#1976d2; border-color:#1976d2; color:#fff; }
.stepper-item--active .stepper-node { background:#fff; border-color:#1976d2; color:#1976d2; box-shadow:0 0 0 4px rgba(25,118,210,.12); }
.stepper-item--denied .stepper-node { background:#e53935; border-color:#e53935; color:#fff; }
.stepper-label { font-size:.8rem; font-weight:600; color:#90a4ae; text-align:center; }
.stepper-item--completed .stepper-label, .stepper-item--active .stepper-label { color:#1976d2; }

.step-panel { padding:28px 32px 32px; animation:fadeInUp .35s ease; }
.step-header { display:flex; align-items:flex-start; gap:16px; margin-bottom:24px; }
.step-number { width:40px; height:40px; border-radius:12px; background:linear-gradient(135deg,#1976d2,#42a5f5); color:#fff; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:1.1rem; flex-shrink:0; }
.step-number--2 { background:linear-gradient(135deg,#5c35c9,#8361e6); }
.step-number--3 { background:linear-gradient(135deg,#00897b,#26a69a); }
.step-number--done { background:linear-gradient(135deg,#388e3c,#66bb6a); font-size:1.2rem; }
.step-title { font-size:1.2rem; font-weight:700; color:#0d2b5e; margin:0; }
.step-desc  { font-size:.9rem; color:#6b7c93; margin:4px 0 0; }

/* Models loader */
.models-loader { padding:0 0 16px; }
.models-loader-inner { background:rgba(25,118,210,.04); border-radius:12px; padding:12px 16px; }
.models-loader-text { font-size:.82rem; color:#1976d2; font-weight:600; margin-top:8px; display:flex; align-items:center; }

.capture-stage {
  background:#f8fafc; border-radius:20px; border:1.5px solid rgba(23,84,180,.08);
  min-height:360px; display:flex; align-items:center; justify-content:center; overflow:hidden;
  transition:border-color .3s;
}
.capture-stage--analyzing { border-color:rgba(25,118,210,.3); }

/* Analyzing state */
.analyzing-state { display:flex; flex-direction:column; align-items:center; text-align:center; padding:40px 24px; }
.analyzing-ring  { position:relative; display:inline-flex; align-items:center; justify-content:center; margin-bottom:20px; }
.analyzing-icon  { position:absolute; }
.analyzing-title { font-size:1.1rem; font-weight:700; color:#0d2b5e; margin:0 0 6px; }
.analyzing-sub   { font-size:.85rem; color:#6b7c93; margin:0 0 20px; }
.analyzing-steps { display:flex; flex-direction:column; gap:8px; text-align:left; }
.analyzing-step  { display:flex; align-items:center; gap:8px; font-size:.83rem; color:#90a4ae; font-weight:500; transition:color .3s; }
.analyzing-step.done { color:#388e3c; }
.analyzing-step.done .v-icon { color:#388e3c !important; }

/* Face match badge */
.face-match-badge {
  display:flex; align-items:center; gap:12px;
  background:rgba(56,142,60,.07); border:1.5px solid rgba(56,142,60,.2);
  border-radius:14px; padding:14px 18px; width:100%;
}
.face-match-title { font-size:.95rem; font-weight:700; color:#2e7d32; }
.face-match-sub   { font-size:.82rem; color:#6b7c93; margin-top:2px; }

.start-stage { display:flex; flex-direction:column; align-items:center; text-align:center; padding:40px 24px; }
.start-icon-wrap { width:88px; height:88px; border-radius:24px; background:linear-gradient(135deg,rgba(25,118,210,.08),rgba(25,118,210,.03)); display:flex; align-items:center; justify-content:center; margin-bottom:20px; }
.start-icon-wrap--2 { background:linear-gradient(135deg,rgba(92,53,201,.08),rgba(92,53,201,.03)); }
.start-icon-wrap--3 { background:linear-gradient(135deg,rgba(0,137,123,.08),rgba(0,137,123,.03)); }
.start-title { font-size:1.15rem; font-weight:700; color:#0d2b5e; margin:0 0 8px; }
.start-hint  { font-size:.9rem; color:#6b7c93; max-width:360px; line-height:1.6; margin:0 0 24px; }
.start-microhint { font-size:.75rem; color:#b0bec5; margin-top:12px; }

.camera-stage { width:100%; display:flex; flex-direction:column; }
.camera-frame { position:relative; width:100%; background:#000; min-height:320px; display:flex; align-items:center; justify-content:center; }
.camera-feed  { width:100%; max-height:420px; object-fit:cover; display:block; }
.camera-guides { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; pointer-events:none; }
.id-guide-rect  { width:320px; height:200px; border:2px dashed rgba(255,255,255,.7); border-radius:12px; position:relative; box-shadow:0 0 0 4000px rgba(0,0,0,.35); }
.guide-label    { position:absolute; bottom:-32px; left:50%; transform:translateX(-50%); color:rgba(255,255,255,.9); font-size:.85rem; font-weight:600; white-space:nowrap; }
.face-guide-oval { width:160px; height:200px; border:2px dashed rgba(255,255,255,.7); border-radius:50%/45%; box-shadow:0 0 0 4000px rgba(0,0,0,.35); }
.recording-pill { position:absolute; top:16px; left:50%; transform:translateX(-50%); background:rgba(0,0,0,.6); color:#fff; padding:6px 14px; border-radius:20px; font-size:.85rem; font-weight:600; display:flex; align-items:center; }
.camera-actions { display:flex; align-items:center; justify-content:space-between; padding:16px 24px; background:#fff; }

.preview-stage  { width:100%; display:flex; flex-direction:column; align-items:center; padding:24px; }
.preview-frame  { position:relative; width:100%; max-width:400px; border-radius:16px; overflow:hidden; box-shadow:0 8px 30px rgba(0,0,0,.12); }
.preview-frame--video { max-width:360px; }
.captured-image { width:100%; max-height:260px; object-fit:cover; display:block; }
.captured-video { width:100%; max-height:260px; display:block; }
.preview-badge  { position:absolute; top:12px; right:12px; background:rgba(56,142,60,.9); color:#fff; padding:4px 10px; border-radius:20px; font-size:.75rem; font-weight:700; display:flex; align-items:center; gap:4px; }
.preview-actions { display:flex; gap:12px; margin-top:20px; }

.denied-state  { display:flex; flex-direction:column; align-items:center; text-align:center; padding:40px 24px; max-width:400px; }
.denied-title  { font-size:1.1rem; font-weight:700; color:#c62828; margin:0 0 8px; }
.denied-text   { font-size:.9rem; color:#6b7c93; line-height:1.6; margin:0 0 20px; }

.geo-success-panel { width:100%; padding:24px; display:flex; flex-direction:column; align-items:center; }
.geo-card { display:flex; align-items:center; gap:16px; background:#fff; border:1.5px solid rgba(56,142,60,.2); border-radius:16px; padding:20px 24px; width:100%; max-width:400px; box-shadow:0 4px 16px rgba(56,142,60,.08); }
.geo-card-icon { width:48px; height:48px; border-radius:14px; background:rgba(56,142,60,.08); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.geo-card-title   { font-size:.9rem; font-weight:700; color:#2e7d32; margin-bottom:4px; }
.geo-card-address { font-size:.95rem; font-weight:600; color:#0d2b5e; display:flex; align-items:center; gap:6px; }
.geo-card-coords  { font-size:.8rem; color:#90a4ae; font-family:monospace; margin-top:4px; }
.flag-img { width:16px; height:12px; border-radius:2px; object-fit:cover; }

.review-stage  { padding:8px 0 0; }
.review-cards  { display:flex; flex-direction:column; gap:12px; }
.review-item   { display:flex; align-items:center; gap:16px; background:#fff; border:1.5px solid rgba(23,84,180,.08); border-radius:16px; padding:14px 18px; }
.review-item--ok { border-color:rgba(56,142,60,.2); background:rgba(56,142,60,.02); }
.review-thumb  { width:56px; height:56px; border-radius:12px; overflow:hidden; background:#e3eaf3; flex-shrink:0; position:relative; }
.review-thumb img, .review-thumb video { width:100%; height:100%; object-fit:cover; }
.review-thumb--video { background:#1a1a2e; }
.review-play   { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; background:rgba(0,0,0,.3); }
.review-thumb--geo { display:flex; align-items:center; justify-content:center; background:rgba(25,118,210,.08); }
.review-label  { font-size:.9rem; font-weight:700; color:#0d2b5e; }
.review-status { font-size:.82rem; color:#6b7c93; display:flex; align-items:center; gap:4px; margin-top:2px; }
.review-disclaimer { border-radius:12px !important; font-size:.85rem; }
.submit-wrap   { display:flex; justify-content:center; margin-top:24px; }
.result-alert  { border-radius:14px !important; font-weight:500; }

.blink { animation:blink 1.2s step-start infinite; }
@keyframes blink { 0%,100%{opacity:1} 50%{opacity:.3} }
@keyframes fadeInUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }

@media(max-width:600px){
  .kyc-header { padding:20px 20px 16px; flex-direction:column; }
  .step-panel { padding:20px 16px 24px; }
  .stepper-nav { padding:16px 16px 0; }
  .id-guide-rect { width:260px; height:164px; }
  .camera-actions { padding:12px 16px; }
  .preview-actions { flex-direction:column; width:100%; }
  .preview-actions .v-btn { width:100%; }
}
</style>